import type { Conversation } from './types';

export type IdentityTier = 'red' | 'yellow' | 'orange' | 'green';

export const TIER_COLORS: Record<IdentityTier, string> = {
  red: '#ef4444',
  yellow: '#eab308',
  // A lighter, clearly-orange amber so it doesn't read as red next to the red tier.
  orange: '#fb923c',
  green: '#22c55e',
};

// Full legend (shown on hover) — the meaning of each traffic-light state, in
// ascending trust order (matches RED < YELLOW < ORANGE < GREEN).
export const TIER_LEGEND: Array<{ tier: IdentityTier; title: string; desc: string }> = [
  { tier: 'red', title: 'Not identified', desc: 'Anonymous — no identifier yet.' },
  { tier: 'yellow', title: 'Identified (self-entered)', desc: 'Customer typed an email themselves — an unverified claim.' },
  { tier: 'orange', title: 'Identified (channel)', desc: 'Authenticated by the channel — WhatsApp/SMS number, Facebook profile, email address.' },
  { tier: 'green', title: 'Verified', desc: 'Proved account ownership — logged in or last-4 of card confirmed.' },
];

// Derives the customer's "identity traffic light" purely from the live chat doc
// (authLevel + channel) so it updates on every message via onSnapshot — no polling.
// Priority: highest wins.
export function identityTier(
  chat: Pick<Conversation, 'authLevel' | 'channel'>
): { tier: IdentityTier; label: string } {
  const authLevel = chat.authLevel; // 'anonymous' | 'identified' | 'verified'
  const channel = chat.channel; // 'web' | 'whatsapp' | 'sms' | 'facebook' | 'email'
  if (authLevel === 'verified') return { tier: 'green', label: 'Verified — logged in or last-4 confirmed' };
  if (channel === 'whatsapp' || channel === 'sms')
    return { tier: 'orange', label: `Identified by ${channel === 'whatsapp' ? 'WhatsApp' : 'SMS'} number` };
  if (channel === 'facebook') return { tier: 'orange', label: 'Identified by Facebook profile' };
  if (channel === 'email') return { tier: 'orange', label: 'Identified by email address' };
  if (authLevel === 'identified') return { tier: 'yellow', label: 'Identified by self-entered email' };
  return { tier: 'red', label: 'Not identified' };
}
